import ResolveAuthenticationModes from '@Application/Security/Sso/Commands/ResolveAuthenticationModes';
import resolveAuthenticationModes from '@Application/Security/Sso/resolveAuthenticationModes';
import type { UserInvitationFailedAction } from '@Application/Users/Users/Actions/userInvitationFailed';
import type { UserUpdateFailedAction } from '@Application/Users/Users/Actions/userUpdateFailed';
import InviteUser from '@Application/Users/Users/Commands/InviteUser';
import UpdateUser from '@Application/Users/Users/Commands/UpdateUser';
import inviteUser from '@Application/Users/Users/inviteUser';
import updateUser from '@Application/Users/Users/updateUser';
import { theme } from '@Guidelines/Theme/theme';
import ignoreHandledError from '@UI/Helpers/ignoreHandledError';
import useDebounce from '@UI/Hooks/useDebounce';
import AuthenticationNotifications from '@UI/Pages/Authentication/Shared/AuthenticationNotifications';
import userSidebarSignal, {
	closeUserSidebar,
} from '@UI/Pages/Administration/Users/Signals/userSidebarSignal';
import deriveAuthenticationMode from '@UI/Pages/Administration/Users/Sidebars/UserSidebar/Helpers/deriveAuthenticationMode';
import deriveOrganizationIds from '@UI/Pages/Administration/Users/Sidebars/UserSidebar/Helpers/deriveOrganizationIds';
import UserFormData from '@UI/Pages/Administration/Users/Sidebars/UserSidebar/FormData/UserFormData';
import isEmailLikelyValid from '@UI/Pages/Administration/Users/Sidebars/UserSidebar/Helpers/isEmailLikelyValid';
import useCurrentFacility from '@UI/Providers/CurrentFacilityProvider/useCurrentFacility';
import type { ComposedOrganization } from '@UI/Providers/CurrentOrganizationProvider/helpers/composeAssignedOrganizations';
import type RoleView from '@UI/Store/Roles/Views/RoleView';
import { css } from '@emotion/react';
import * as React from 'react';
import { updateValidationFromExternalSource } from '@verclam/form-validator';
import { useSignalValue } from '@verclam/pulsar';
import { useDispatch, useSelector } from '@verclam/synapse';
import {
	Button,
	Checkbox,
	type CheckboxValue,
	confirmDialog,
	Dropdown,
	Fieldset,
	Form,
	InputText,
	Sidebar,
	Skeleton,
	Switch,
} from '@verclam/ui-kit';
import useStrictAuthenticatedUser from '@UI/Providers/AuthenticatedUserProvider/useStrictAuthenticatedUser';

const loadingFieldStyle = css`
	display: flex;
	flex-direction: column;
	gap: 0.5rem;
	margin: .75rem 1.25rem;
`;

const loadingFieldLabelStyle = css`
	font-size: ${theme.fontSizes.small};
	font-weight: ${theme.fontWeights.medium};
	color: ${theme.colors.inkSecondary};
`;

const introTextStyle = css`
	margin: .75rem 1.25rem;
	font-family: ${theme.fonts.body};
	font-size: ${theme.fontSizes.medium};
	color: ${theme.colors.inkSecondary};
`;

const fieldsetStyle = css`
	margin: 0 1.25rem;
`;

const organizationGroupStyle = css`
	display: flex;
	flex-direction: column;
	gap: 0.375rem;
	margin-bottom: 0.875rem;

	&:last-child {
		margin-bottom: 0;
	}
`;

const organizationNameStyle = css`
	margin: 0;
	font-family: ${theme.fonts.body};
	font-weight: ${theme.fontWeights.medium};
	font-size: ${theme.fontSizes.small};
	color: ${theme.colors.inkSecondary};
`;

const facilitiesCheckboxesStyle = css`
	display: flex;
	flex-direction: column;
	gap: 0.25rem;
`;

// Two options only, written in French directly in the component: the catalogue served by
// `GET /api/enums/user-authentication-modes` still carries three, none of which fits an option
// that covers two values, so this screen no longer consumes it.
const CREDENTIAL_TYPE_OPTIONS = [
	{ value: 'password', label: 'Mot de passe' },
	{ value: 'sso', label: "Fournisseur d'identité (SSO)" },
] as const;

/**
 * The server still validates and reports errors under `authenticationMode` — its own DTO field
 * unaware of the two controls this panel decomposes that value into.
 * Remapped onto `credentialType`, the field the Dropdown actually validates, before
 * `updateValidationFromExternalSource` sees it: left as `authenticationMode`, the field error
 * would silently be dropped (no such field on `UserFormData` any more).
 */
const remapAuthenticationModeFieldError = <
	TAction extends UserInvitationFailedAction | UserUpdateFailedAction,
>(
	action: TAction,
): TAction => {
	if (action.payload === undefined) {
		return action;
	}

	return {
		...action,
		payload: {
			...action.payload,
			fieldErrors: action.payload.fieldErrors.map((fieldError) =>
				fieldError.field === 'authenticationMode'
					? { ...fieldError, field: 'credentialType' }
					: fieldError,
			),
		},
	};
};

/** The shape the Dropdown actually reads (`optionLabel="label"` / `optionValue="id"`), plus the
 * optional `disabled` flag it reads by default when no `optionDisabled` prop is supplied. */
type RoleDropdownOption = {
	readonly id: number;
	readonly label: string;
	readonly disabled?: boolean;
};

type Props = {
	readonly roles: readonly RoleView[];
	/** Every organization the caller is assigned to, each carrying its facilities
	 * (`useCurrentOrganization().organizations`, composed client-side). */
	readonly assignableOrganizations: readonly ComposedOrganization[];
	readonly areOptionsLoading: boolean;
};

/**
 * One panel for both invitation and edition, told apart by `userId` on the signal's context.
 * Reads the edited user straight from the store rather than through a prop: the page mounts this
 * component once, with no data of its own to pass it.
 */
const UserSidebar: React.FC<Props> = ({ roles, assignableOrganizations, areOptionsLoading }) => {
	const dispatch = useDispatch();
	const context = useSignalValue(userSidebarSignal);
	const users = useSelector((state) => state.usersStore.users);
	const { user } = useStrictAuthenticatedUser();
	const { currentFacility } = useCurrentFacility();

	const [formData, setFormData] = React.useState(() => UserFormData.createEmpty());
	const [isSubmitting, setIsSubmitting] = React.useState(false);

	const formId = React.useId();
	const userId = context?.userId ?? null;
	const editedUser = userId !== null ? (users?.findById(userId) ?? null) : null;
	const isEditMode = editedUser !== null;

	React.useEffect(() => {
		if (context === null) {
			return;
		}

		if (editedUser !== null) {
			setFormData(UserFormData.fromView(editedUser));
			return;
		}

		// The current facility is checked off by default when opening a fresh invitation.
		setFormData(UserFormData.createEmpty(currentFacility !== null ? [currentFacility.id] : []));
	}, [context, editedUser, currentFacility]);

	// Preselects `sso` on invitation only, once the typed address looks valid: `resolveAuthenticationModes`
	// is the public route (§8.6) — never `GET …/identity-provider`, which `SSO_MANAGEMENT` alone may read.
	// A failure never blocks the panel: the toast is shown by `AuthenticationNotifications`, and the mode
	// simply stays on its default `password`. This automatic selection only ever touches `credentialType`
	// — `isSecondFactorRequired` is never reset here, same rule as `handleCredentialTypeChange`.
	const debouncedEmailForPreselection = useDebounce(formData.email, 400);

	React.useEffect(() => {
		if (isEditMode || !isEmailLikelyValid(debouncedEmailForPreselection)) {
			return;
		}

		dispatch(resolveAuthenticationModes(new ResolveAuthenticationModes(debouncedEmailForPreselection)))
			.then((action) => {
				if (action.type !== 'AUTHENTICATION_MODES_RESOLVED') {
					return;
				}

				if (action.payload.ssoEnabled) {
					setFormData((current) => current.setCredentialType('sso'));
				}
			})
			.catch(ignoreHandledError); // toast displayed by AuthenticationNotifications
	}, [debouncedEmailForPreselection, isEditMode, dispatch]);

	const handleFirstNameChange = React.useCallback((value: string) => {
		setFormData((current) => current.setFirstName(value));
	}, []);

	const handleLastNameChange = React.useCallback((value: string) => {
		setFormData((current) => current.setLastName(value));
	}, []);

	const handleEmailChange = React.useCallback((value: string) => {
		setFormData((current) => current.setEmail(value));
	}, []);

	const handleRoleIdChange = React.useCallback((value: number | null) => {
		setFormData((current) => current.setRoleId(value));
	}, []);

	const handleCredentialTypeChange = React.useCallback((value: string | null) => {
		if (value !== 'password' && value !== 'sso') {
			return;
		}

		// Only the mode is touched here — never `isSecondFactorRequired`: the switch's rendering
		// changes (it greys out on `sso`), but its value survives every round trip between modes.
		setFormData((current) => current.setCredentialType(value));
	}, []);

	const handleIsSecondFactorRequiredChange = React.useCallback((checked: boolean) => {
		setFormData((current) => current.setIsSecondFactorRequired(checked));
	}, []);

	const handleFacilityToggle = React.useCallback((checked: boolean, value: CheckboxValue | undefined) => {
		// `Checkbox.value` is typed `string | number` at the contract level, but every facility
		// checkbox here is keyed by its numeric id — never a string one.
		if (typeof value !== 'number') {
			return;
		}

		setFormData((current) =>
			current.setFacilityIds(
				checked
					? [...current.facilityIds, value]
					: current.facilityIds.filter((facilityId) => facilityId !== value),
			),
		);
	}, []);

	const handleSubmit = React.useCallback(() => {
		if (!formData.isFormDataValid()) {
			setFormData(formData.showErrors());
			return;
		}

		// Unreachable in practice: `AppShell` never mounts this page without an account.
		if (user.account === null) {
			return;
		}

		// Narrowed by `isFormDataValid()` above: the `roleId` constraint rejects `null`.
		if (formData.roleId === null) {
			return;
		}

		const accountId = user.account.id;
		const roleId = formData.roleId;
		// Union of the organizations owning a checked facility and the organizations the
		// user already carries — never a plain derivation from the checkboxes alone.
		const organizationIds = deriveOrganizationIds(
			formData.facilityIds,
			assignableOrganizations,
			editedUser?.organizationIds ?? [],
		);

		// The single value the API expects, composed from the two screen controls. The Command
		// always carries this bare value — never `formData.credentialType`, which never leaves the front.
		const authenticationMode = deriveAuthenticationMode.compose(
			formData.credentialType,
			formData.isSecondFactorRequired,
		);

		const submitUser = () => {
			setIsSubmitting(true);

			if (userId === null) {
				dispatch(
					inviteUser(
						new InviteUser(
							accountId,
							formData.email,
							formData.firstName,
							formData.lastName,
							roleId,
							organizationIds,
							formData.facilityIds,
							authenticationMode,
						),
					),
				)
					.then(() => closeUserSidebar())
					.catch((action: UserInvitationFailedAction) =>
						updateValidationFromExternalSource({
							action: remapAuthenticationModeFieldError(action),
							eventNames: 'USER_INVITATION_FAILED',
							formDataUpdater: setFormData,
						}),
					)
					.finally(() => setIsSubmitting(false));
				return;
			}

			dispatch(
				updateUser(
					new UpdateUser(
						accountId,
						userId,
						formData.firstName,
						formData.lastName,
						formData.email,
						roleId,
						organizationIds,
						formData.facilityIds,
						authenticationMode,
					),
				),
			)
				// A `422` refusal (transition rejected on edition) carries no `fieldErrors` — only a `message`,
				// which the toast shown by `UsersNotifications` already renders: nothing left to apply here.
				// A `400` refusal (mode unavailable, invitation only) does carry `fieldErrors.credentialType`,
				// which `updateValidationFromExternalSource` applies to the field below. Both paths run through
				// the very same `.catch()`, exactly as `USER_INVITATION_FAILED` already does above.
				.then(() => closeUserSidebar())
				.catch((action: UserUpdateFailedAction) =>
					updateValidationFromExternalSource({
						action: remapAuthenticationModeFieldError(action),
						eventNames: 'USER_UPDATE_FAILED',
						formDataUpdater: setFormData,
					}),
				)
				.finally(() => setIsSubmitting(false));
		};

		// Invitation carries no previous mode to compare against: it is never a "change". The comparison
		// runs against the *derived* value, never against `formData.credentialType` alone: switching only
		// the switch (`password_and_second_factor` → `password_only`) leaves the dropdown untouched but is
		// still a destructive transition, and must still be caught here.
		const previousAuthenticationMode = editedUser?.authenticationMode.value ?? null;
		const authenticationModeChanged =
			previousAuthenticationMode !== null && previousAuthenticationMode !== authenticationMode;

		if (!authenticationModeChanged) {
			submitUser();
			return;
		}

		// A mode change always destroys something — a password, a second factor, or both: a
		// destructive action, confirmed explicitly rather than folded into an ordinary save
		// (`core.md` front §7). One text per destination, never per source.
		let destinationWarning: string;
		if (authenticationMode === 'sso') {
			destinationWarning =
				"Le mot de passe de cette personne sera supprimé, ainsi que son second facteur s'il est configuré. Elle se connectera par le fournisseur d'identité de la structure.";
		} else if (authenticationMode === 'password_only') {
			destinationWarning =
				'Le second facteur de cette personne sera supprimé. Elle se connectera avec son seul mot de passe.';
		} else {
			destinationWarning =
				'Cette personne devra configurer un second facteur à sa prochaine connexion.';
		}

		const neverConnectedWarning =
			editedUser !== null && editedUser.activatedAt === null
				? ' Une nouvelle invitation lui sera envoyée.'
				: '';
		// Only firing when the *origin* was `sso`: between the two password variants, the password
		// itself is conserved, so this suffix must not fire there.
		const fromSsoWarning =
			previousAuthenticationMode === 'sso'
				? ' Elle devra définir un mot de passe via « Mot de passe oublié » avant de pouvoir se connecter.'
				: '';

		confirmDialog({
			header: 'Confirmer le changement de mode de connexion',
			message: `${destinationWarning}${neverConnectedWarning}${fromSsoWarning}`,
			acceptLabel: 'Confirmer',
			rejectLabel: 'Annuler',
			accept: submitUser,
		});
	}, [formData, userId, user, dispatch, assignableOrganizations, editedUser]);

	const isCredentialTypeSso = formData.credentialType === 'sso';
	const facilitiesErrorMessage = formData.getValidationMessage('facilityIds');

	// A platform-level role (a super-administrator's) belongs to no account and is therefore never
	// returned by this account-scoped catalogue. Without this synthetic, non-selectable entry the
	// dropdown would render as empty even though the edited user does carry a role.
	const roleOptions = React.useMemo<readonly RoleDropdownOption[]>(() => {
		if (formData.roleId === null || roles.some((role) => role.id === formData.roleId)) {
			return roles;
		}

		return [{ id: formData.roleId, label: 'Rôle inconnu', disabled: true }, ...roles];
	}, [roles, formData.roleId]);

	// Organizations composed with no facility at all carry an empty group — omitted here rather than
	// rendered as a header with nothing selectable underneath, the same rule `FacilitySelector` applies.
	const organizationsWithFacilities = React.useMemo(
		() => assignableOrganizations.filter((organization) => organization.facilities.length > 0),
		[assignableOrganizations],
	);
	const hasNoAssignableFacility = organizationsWithFacilities.length === 0;

	return (
		<React.Fragment>
			<Sidebar
				visible={context !== null}
				onHide={closeUserSidebar}
				position="right"
				header={isEditMode ? 'Modifier l’utilisateur' : 'Inviter un utilisateur'}
				footer={
					<React.Fragment>
						<Button
							type="button"
							severity="secondary"
							outlined
							label="Annuler"
							onClick={closeUserSidebar}
						/>
						<Button type="submit" form={formId} label="Enregistrer" loading={isSubmitting} />
					</React.Fragment>
				}
				blockScroll
			>
				<Form
					id={formId}
					onSubmit={handleSubmit}
					aria-label={isEditMode ? 'Modifier un utilisateur' : 'Inviter un utilisateur'}
				>
					<InputText
						name="firstName"
						label="Prénom"
						required
						value={formData.firstName}
						onChange={handleFirstNameChange}
						errorMessage={formData.getValidationMessage('firstName')}
						isFormFieldValid={formData.isValid('firstName')}
					/>
					<InputText
						name="lastName"
						label="Nom"
						required
						value={formData.lastName}
						onChange={handleLastNameChange}
						errorMessage={formData.getValidationMessage('lastName')}
						isFormFieldValid={formData.isValid('lastName')}
					/>
					<InputText
						name="email"
						label="Adresse e-mail"
						type="email"
						required
						disabled={isEditMode && editedUser?.activatedAt !== null}
						subLabel={
							isEditMode && editedUser?.isNeverActivated
								? 'Modifier l’adresse annule le lien envoyé et envoie une nouvelle invitation.'
								: undefined
						}
						value={formData.email}
						onChange={handleEmailChange}
						errorMessage={formData.getValidationMessage('email')}
						isFormFieldValid={formData.isValid('email')}
					/>
					<Dropdown
						name="credentialType"
						label="Mode de connexion"
						required
						options={CREDENTIAL_TYPE_OPTIONS}
						optionLabel="label"
						optionValue="value"
						value={formData.credentialType}
						onChange={handleCredentialTypeChange}
						errorMessage={formData.getValidationMessage('credentialType')}
						isFormFieldValid={formData.isValid('credentialType')}
					/>
					<Switch
						name="isSecondFactorRequired"
						label="Second facteur exigé"
						// Rendering only, never the stored value: on `sso` the switch always
						// displays on, but `formData.isSecondFactorRequired` underneath is untouched.
						checked={isCredentialTypeSso ? true : formData.isSecondFactorRequired}
						onChange={handleIsSecondFactorRequiredChange}
						disabled={isCredentialTypeSso}
						subLabel={
							isCredentialTypeSso
								? "Le second facteur est géré par le fournisseur d'identité."
								: undefined
						}
						isFormFieldValid={true}
					/>
					{areOptionsLoading ? (
						<div css={loadingFieldStyle}>
							<span css={loadingFieldLabelStyle}>Rôle *</span>
							<Skeleton height={theme.controls.height} />
						</div>
					) : (
						<Dropdown
							name="roleId"
							label="Rôle"
							required
							options={roleOptions}
							optionLabel="label"
							optionValue="id"
							value={formData.roleId}
							onChange={handleRoleIdChange}
							errorMessage={formData.getValidationMessage('roleId')}
							isFormFieldValid={formData.isValid('roleId')}
						/>
					)}
					{areOptionsLoading ? (
						<div css={loadingFieldStyle}>
							<span css={loadingFieldLabelStyle}>Agences *</span>
							<Skeleton height={theme.controls.height} />
						</div>
					) : (
						<Fieldset legend="Agences *" css={fieldsetStyle}>
							{hasNoAssignableFacility ? (
								<p css={introTextStyle}>
									Aucune agence ne vous est affectée : vous ne pouvez pas encore inviter
									d'utilisateur.
								</p>
							) : (
								organizationsWithFacilities.map((organization) => (
									<div key={organization.id} css={organizationGroupStyle}>
										<p css={organizationNameStyle}>{organization.name}</p>
										<div css={facilitiesCheckboxesStyle}>
											{organization.facilities.map((facility) => (
												<Checkbox
													key={facility.id}
													name="facilityIds"
													label={facility.name}
													value={facility.id}
													checked={formData.facilityIds.includes(facility.id)}
													onChange={handleFacilityToggle}
												/>
											))}
										</div>
									</div>
								))
							)}
							{facilitiesErrorMessage !== undefined && (
								<small className="ui-kit-field-error">{facilitiesErrorMessage}</small>
							)}
						</Fieldset>
					)}
				</Form>
			</Sidebar>
			<AuthenticationNotifications />
		</React.Fragment>
	);
};

export default UserSidebar;
